<!--
  Debt Consolidation calculator.

  Two phases in one page:
    1. "debt"    -- a five-step wizard that totals what the borrower owes today, then a review.
    2. "options" -- what a consolidation would look like: a new consolidated mortgage, the
                    keep-the-mortgage-plus-HELOC alternative, and a three-way comparison.

  It is a self-contained arithmetic tool. No Encompass, no Polly, no loanId, no API call and no
  saved state -- which is why it can live under a plain /calculators/* route with no gate.

  Shape: CATEGORIES is the single source of truth for phase 1. Each entry owns its copy, icon,
  fields, how its monthly figure rolls up and how its review line reads; the wizard, progress
  bar, running-total rail, review list and every total derive from it. Phase 2 is built out of
  the pure money functions at the top of the script (pmt / monthsToPayoff / blendedRate) --
  nothing in there reads component state, so the arithmetic is testable on its own.
-->
<template>
  <div class="calc-page">
    <!-- Same header block every 2.0 page uses (LoRedirect, PccdDashboard, Leaderboard): an
         icon tile plus title and subtitle, wrapped in PageHeaderPortal so it relocates into
         the top bar under sidebar nav and renders in place otherwise. header.vue already
         neutralises .page-header-left inside the slot, so no new rule is needed there. -->
    <PageHeaderPortal>
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-calculator"></i>
        </div>

        <div>
          <h1 class="page-title">Debt Consolidation</h1>
          <p class="page-subtitle">Total what the borrower owes today, then compare consolidation options</p>
        </div>
      </div>
    </PageHeaderPortal>

    <!-- ══════════════════════════ PHASE 1 — CURRENT DEBT ══════════════════════════ -->
    <template v-if="phase === 'debt'">
      <section class="page-card progress-card">
        <div class="progress-top">
          <span class="progress-step">{{ isReview ? 'Complete' : `Step ${stepIndex + 1} of ${categories.length}` }}</span>
          <span class="progress-pct">{{ isReview ? 'All categories reviewed' : `${percent}% complete` }}</span>
        </div>

        <div class="progress-track" role="progressbar" :aria-valuenow="percent" aria-valuemin="0" aria-valuemax="100">
          <div class="progress-fill" :style="{ width: percent + '%' }"></div>
        </div>

        <ol class="progress-steps">
          <li v-for="(c, i) in categories"
              :key="c.key"
              :class="{ 'is-current': i === stepIndex, 'is-visited': i <= maxVisited }">
            <button type="button" :disabled="i > maxVisited" @click="goTo(i)">{{ i + 1 }}. {{ c.nav }}</button>
          </li>
        </ol>
      </section>

      <div class="calc-body">
        <section class="page-card step-card">
          <template v-if="!isReview">
            <div class="step-head">
              <span class="step-icon" :class="'tone-' + cat.tone"><i :class="cat.icon"></i></span>
              <div class="step-head-text">
                <p class="eyebrow">Debt Category</p>
                <h2>{{ cat.title }}</h2>
                <p class="step-desc">{{ cat.description }}</p>
              </div>
            </div>

            <div class="field-grid">
              <label v-for="f in cat.fields" :key="f.key" class="field" :class="{ 'field--new-row': f.newRow }">
                <span class="field-label">
                  {{ f.label }}
                  <button v-if="f.info"
                          type="button"
                          class="info-dot"
                          :aria-label="f.info"
                          :aria-expanded="openInfo === f.key ? 'true' : 'false'"
                          @click.prevent="openInfo = openInfo === f.key ? '' : f.key">i</button>
                </span>
                <span class="input-shell">
                  <span v-if="f.prefix" class="affix affix--pre">{{ f.prefix }}</span>
                  <input type="text"
                         inputmode="decimal"
                         autocomplete="off"
                         :value="values[cat.key][f.key]"
                         @input="onInput(cat, f, $event.target.value)"
                         @blur="onBlur(cat, f)" />
                  <span v-if="f.suffix" class="affix affix--post">{{ f.suffix }}</span>
                </span>
                <small v-if="f.info && openInfo === f.key" class="field-note">{{ f.info }}</small>
              </label>
            </div>
          </template>

          <template v-else>
            <div class="step-head">
              <span class="step-icon tone-green"><i class="fa-light fa-circle-check"></i></span>
              <div class="step-head-text">
                <p class="eyebrow">Summary</p>
                <h2>Debt consolidation summary</h2>
                <p class="step-desc">Review the monthly payments, balances, and rates below. Select Edit to make a change.</p>
              </div>
            </div>

            <ul class="review-list">
              <li v-for="(c, i) in categories" :key="c.key" class="review-row">
                <span class="review-name">{{ c.title }}</span>
                <span class="review-detail">{{ reviewDetail(c) }}</span>
                <strong class="review-amount">{{ money(monthlyFor(c)) }}</strong>
                <button type="button" class="link-button" @click="goTo(i)">Edit</button>
              </li>
            </ul>
          </template>

          <div class="step-foot">
            <button type="button" class="secondary-button" :disabled="stepIndex === 0" @click="back">Back</button>
            <div class="foot-right">
              <button type="button" class="primary-button" @click="next">
                {{ isReview ? 'Continue to Loan Options' : (stepIndex === categories.length - 1 ? 'Review Totals' : 'Continue') }}
              </button>
            </div>
          </div>
        </section>

        <aside class="page-card rail">
          <div class="rail-head">
            <div>
              <p class="eyebrow">Running Total</p>
              <h3>Your current debt</h3>
            </div>
            <span class="rail-chip">{{ filledCount }} of {{ categories.length }}</span>
          </div>

          <ul class="rail-list">
            <li v-for="(c, i) in categories" :key="c.key" class="rail-row" :class="{ 'is-done': isFilled(c) }">
              <span class="rail-num">
                <i v-if="isFilled(c)" class="fa-solid fa-check"></i>
                <template v-else>{{ i + 1 }}</template>
              </span>
              <span class="rail-name">{{ c.title }}</span>
              <span class="rail-right">
                <strong>{{ isFilled(c) ? money(monthlyFor(c)) : '—' }}</strong>
                <small>{{ railSub(c) }}</small>
              </span>
            </li>
          </ul>

          <div class="rail-foot">
            <div>
              <span>Total monthly payments</span>
              <strong>{{ money(totalMonthly) }}</strong>
            </div>
            <div>
              <span>Total outstanding balance</span>
              <strong>{{ money(totalBalance) }}</strong>
            </div>
          </div>
        </aside>
      </div>
    </template>

    <!-- ══════════════════════════ PHASE 2 — LOAN OPTIONS ══════════════════════════ -->
    <template v-else>
      <section class="page-card options-head">
        <div>
          <p class="eyebrow">New Consolidated Debt</p>
          <h2>Consolidated Mortgage</h2>
        </div>
        <button type="button" class="secondary-button" @click="editCurrentDebt">Edit Current Debt</button>
      </section>

      <!-- ── comparison ──────────────────────────────────────────────────────── -->
      <section class="page-card compare-card">
        <p class="eyebrow">Side-by-side comparison</p>
        <h2 class="compare-title">Compare all three scenarios</h2>

        <!-- What happens if the borrower keeps paying today's total outlay, but against the
             new, cheaper consolidated mortgage. -->
        <div class="accel">
          <div class="accel-head">
            <div>
              <p class="eyebrow eyebrow--gold">Accelerated payment</p>
              <h3>Pay the new mortgage down faster</h3>
            </div>
            <span class="accel-chip">Same monthly outlay</span>
          </div>

          <div class="accel-grid">
            <div class="accel-cell accel-cell--lead">
              <span class="accel-label">Estimated payoff time</span>
              <strong class="accel-value" :class="{ 'is-na': !accel.available }">
                {{ accel.available ? accel.payoffLabel : 'Not available' }}
              </strong>
              <small v-if="!accel.available">The current monthly outlay does not cover the new required payment</small>
            </div>
            <div class="accel-cell">
              <span class="accel-label">Current total monthly payments</span>
              <strong class="accel-value accel-value--sm">{{ money(totalMonthly) }}</strong>
            </div>
            <div class="accel-cell">
              <span class="accel-label">Applied to principal &amp; interest</span>
              <strong class="accel-value accel-value--sm">{{ money(accel.appliedToPi) }}</strong>
            </div>
            <div class="accel-cell">
              <span class="accel-label">Time saved</span>
              <strong class="accel-value accel-value--sm">{{ accel.available ? accel.savedLabel : '—' }}</strong>
            </div>
            <div class="accel-cell">
              <span class="accel-label">Estimated interest saved</span>
              <strong class="accel-value accel-value--sm">{{ money(accel.interestSaved) }}</strong>
            </div>
          </div>

          <p class="accel-foot">
            At least {{ money(newMortgage.totalMonthly) }} per month is needed for the selected loan terms, taxes, and insurance.
          </p>
        </div>

        <div class="compare-grid">
          <div class="compare-col">
            <div class="compare-col-head">
              <div>
                <p class="eyebrow">Current</p>
                <h4>Current Debt</h4>
              </div>
            </div>
            <div class="stat-box">
              <span>Monthly payments</span>
              <strong>{{ money(totalMonthly) }}</strong>
            </div>
            <dl class="compare-rows">
              <div><dt>Outstanding balance</dt><dd>{{ money(totalBalance) }}</dd></div>
              <div><dt>Blended interest rate</dt><dd>{{ rate(currentBlendedRate) }}</dd></div>
              <div><dt>Mortgage payment</dt><dd>{{ money(monthlyFor(categories[0])) }}</dd></div>
              <div><dt>Non-mortgage payments</dt><dd>{{ money(nonMortgageMonthly) }}</dd></div>
            </dl>
          </div>

          <div class="compare-col compare-col--option1">
            <div class="compare-col-head">
              <div>
                <p class="eyebrow">Option 1</p>
                <h4>Cash Out Refinance</h4>
              </div>
              <button type="button" class="icon-button" title="Edit the new mortgage terms" @click="scrollTo('mortgageCalc')">
                <i class="fa-light fa-pen"></i>
              </button>
            </div>
            <div class="stat-box">
              <span>New monthly payment</span>
              <strong>{{ money(newMortgage.totalMonthly) }}</strong>
            </div>
            <dl class="compare-rows">
              <div><dt>New loan amount</dt><dd>{{ money(newMortgage.amount) }}</dd></div>
              <div><dt>Interest rate</dt><dd>{{ rate(num(refi.rate)) }}</dd></div>
              <div><dt>Loan term</dt><dd>{{ refi.term }} years</dd></div>
              <div>
                <dt>Monthly difference</dt>
                <dd :class="diffTone(refiDifference)">{{ diffLabel(refiDifference) }}</dd>
              </div>
            </dl>
          </div>

          <div class="compare-col">
            <div class="compare-col-head">
              <div>
                <p class="eyebrow">Option 2</p>
                <h4>Current Mortgage + HELOC</h4>
              </div>
              <button type="button" class="icon-button" title="Edit the HELOC terms" @click="scrollTo('helocCalc')">
                <i class="fa-light fa-pen"></i>
              </button>
            </div>
            <div class="stat-box">
              <span>Combined monthly payment</span>
              <strong>{{ money(helocPlan.combinedMonthly) }}</strong>
            </div>
            <dl class="compare-rows">
              <div><dt>HELOC payment</dt><dd>{{ money(helocPlan.payment) }}</dd></div>
              <div><dt>Combined balance</dt><dd>{{ money(helocPlan.combinedBalance) }}</dd></div>
              <div><dt>Blended interest rate</dt><dd>{{ rate(helocPlan.blendedRate) }}</dd></div>
              <div>
                <dt>Monthly difference</dt>
                <dd :class="diffTone(helocDifference)">{{ diffLabel(helocDifference) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <!-- ── new mortgage ────────────────────────────────────────────────────── -->
      <div class="calc-body" ref="mortgageCalc">
        <section class="page-card step-card">
          <div class="step-head">
            <span class="step-icon tone-green"><i class="fa-light fa-house"></i></span>
            <div class="step-head-text">
              <p class="eyebrow">Mortgage Calculator</p>
              <h2>Build the new mortgage</h2>
              <p class="step-desc">
                The new loan amount starts with the total outstanding debt. Adjust the terms below to
                calculate the consolidated monthly payment.
              </p>
            </div>
          </div>

          <div class="field-grid field-grid--wide">
            <label class="field field--full">
              <span class="field-label">Calculated loan amount</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="refi.amount"
                       @input="onOptionInput('refi', 'amount', $event.target.value)"
                       @blur="refi.amount = formatMoneyField(refi.amount)" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">Interest rate</span>
              <span class="input-shell">
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="refi.rate"
                       @input="refi.rate = $event.target.value"
                       @blur="refi.rate = formatRateField(refi.rate)" />
                <span class="affix affix--post">%</span>
              </span>
            </label>

            <label class="field">
              <span class="field-label">Loan term</span>
              <span class="input-shell input-shell--select">
                <select v-model="refi.term">
                  <option v-for="t in TERMS" :key="t" :value="t">{{ t }} years</option>
                </select>
              </span>
            </label>

            <label class="field">
              <span class="field-label">Annual property taxes</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="refi.taxes"
                       @input="onOptionInput('refi', 'taxes', $event.target.value)"
                       @blur="refi.taxes = formatMoneyField(refi.taxes)" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">Annual homeowners insurance</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="refi.insurance"
                       @input="onOptionInput('refi', 'insurance', $event.target.value)"
                       @blur="refi.insurance = formatMoneyField(refi.insurance)" />
              </span>
            </label>
          </div>
        </section>

        <aside class="page-card rail rail--dark">
          <div class="rail-head">
            <div>
              <p class="eyebrow eyebrow--gold">New Payment</p>
              <h3>Consolidated mortgage</h3>
            </div>
          </div>

          <div class="rail-hero">
            <span>Estimated monthly payment</span>
            <strong>{{ money(newMortgage.totalMonthly) }}</strong>
          </div>

          <dl class="rail-rows">
            <div><dt>Principal &amp; interest</dt><dd>{{ money(newMortgage.pi) }}</dd></div>
            <div><dt>Property taxes</dt><dd>{{ money(newMortgage.taxesMonthly) }}</dd></div>
            <div><dt>Homeowners insurance</dt><dd>{{ money(newMortgage.insuranceMonthly) }}</dd></div>
          </dl>

          <dl class="rail-rows rail-rows--divided">
            <div><dt>Current monthly payments</dt><dd>{{ money(totalMonthly) }}</dd></div>
          </dl>

          <div class="rail-savings">
            <span>Estimated monthly savings</span>
            <strong>{{ money(Math.max(0, refiDifference)) }}</strong>
          </div>
        </aside>
      </div>

      <!-- ── HELOC ───────────────────────────────────────────────────────────── -->
      <section class="page-card banner-card">
        <p class="eyebrow">Alternate scenario</p>
        <h2>Keep Current Mortgage + HELOC</h2>
      </section>

      <section class="page-card heloc-card" ref="helocCalc">
        <div class="heloc-split">
          <div class="heloc-intro">
            <span class="step-icon tone-green"><i class="fa-light fa-plus"></i></span>
            <p class="eyebrow">HELOC Details</p>
            <h2>Pay off non-mortgage debt</h2>
            <p class="step-desc">
              The HELOC amount starts with the outstanding balances from credit cards, car loans,
              personal loans, and other debts.
            </p>
          </div>

          <div class="field-grid">
            <label class="field">
              <span class="field-label">HELOC amount</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="heloc.amount"
                       @input="onOptionInput('heloc', 'amount', $event.target.value)"
                       @blur="heloc.amount = formatMoneyField(heloc.amount)" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">HELOC interest rate</span>
              <span class="input-shell">
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="heloc.rate"
                       @input="heloc.rate = $event.target.value"
                       @blur="heloc.rate = formatRateField(heloc.rate)" />
                <span class="affix affix--post">%</span>
              </span>
            </label>

            <label class="field">
              <span class="field-label">Payment structure</span>
              <span class="input-shell input-shell--select">
                <select v-model="heloc.structure">
                  <option value="pi">Principal &amp; interest</option>
                  <option value="io">Interest only</option>
                </select>
              </span>
            </label>

            <label class="field">
              <span class="field-label">Repayment term</span>
              <span class="input-shell input-shell--select">
                <select v-model="heloc.term" :disabled="heloc.structure === 'io'">
                  <option v-for="t in HELOC_TERMS" :key="t" :value="t">{{ t }} years</option>
                </select>
              </span>
            </label>
          </div>
        </div>

        <div class="step-foot">
          <button type="button" class="secondary-button" @click="editCurrentDebt">Back to Current Debt</button>
          <div class="foot-right">
            <button type="button" class="secondary-button" @click="copySummary">
              {{ copied ? 'Copied' : 'Copy Summary' }}
            </button>
            <button type="button" class="primary-button" @click="startOver">Start Over</button>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
  import PageHeaderPortal from "@/views/PageHeaderPortal.vue"

  // Everything the user types is held as a string exactly as typed, so a half-finished "1,2"
  // or "." does not get rewritten under the caret. num() is the one place a string becomes a
  // number, and it is deliberately forgiving: commas, a dollar sign and stray spaces all strip.
  function num(v) {
    const n = parseFloat(String(v == null ? "" : v).replace(/[^0-9.-]/g, ""))
    return Number.isFinite(n) ? n : 0
  }

  function hasValue(v) {
    return String(v == null ? "" : v).trim() !== ""
  }

  // ── money maths. Pure: nothing below reads component state, so it is testable on its own. ──

  // Standard amortized payment. A 0% loan is straight-line, which the closed form cannot do
  // (it divides by zero), so it is handled separately rather than left to produce NaN.
  function pmt(principal, annualRatePct, years) {
    const n = Math.round(years * 12)
    const r = annualRatePct / 100 / 12
    if (principal <= 0 || n <= 0) return 0
    if (!r) return principal / n
    return (principal * r) / (1 - Math.pow(1 + r, -n))
  }

  // Months to clear `principal` paying `payment` each month. Returns null when the payment
  // never gets there -- at or below the monthly interest the balance grows, and the log below
  // would return NaN rather than a very large number, so the caller must handle the null.
  function monthsToPayoff(principal, annualRatePct, payment) {
    const r = annualRatePct / 100 / 12
    if (principal <= 0 || payment <= 0) return null
    if (!r) return principal / payment
    if (payment <= principal * r) return null
    return -Math.log(1 - (principal * r) / payment) / Math.log(1 + r)
  }

  // Balance-weighted average rate. Zero balances contribute nothing, which is what makes this
  // the right average for a blended rate rather than a plain mean of the rates.
  function blendedRate(parts) {
    let bal = 0
    let weighted = 0
    for (const p of parts) {
      bal += p.balance
      weighted += p.balance * p.rate
    }
    return bal > 0 ? weighted / bal : 0
  }

  function monthsLabel(months) {
    const m = Math.max(0, Math.round(months))
    const y = Math.floor(m / 12)
    const rem = m % 12
    if (!y) return `${rem} mo${rem === 1 ? "" : "s"}`
    if (!rem) return `${y} yr${y === 1 ? "" : "s"}`
    return `${y} yr${y === 1 ? "" : "s"} ${rem} mo${rem === 1 ? "" : "s"}`
  }

  const CURRENCY = { kind: "currency", prefix: "$", decimals: 2 }
  const RATE = { kind: "rate", suffix: "%", decimals: 3 }
  const PER_MONTH = { kind: "currency", prefix: "$", suffix: "/month", decimals: 2 }

  // ── Phase 1 categories. Order here is the order of the wizard, the rail and the review. ──
  const CATEGORIES = [
    {
      key: "mortgage",
      nav: "Mortgage",
      title: "Current Mortgage Payment",
      icon: "fa-light fa-house",
      tone: "blue",
      description:
        "Enter the monthly principal and interest payment, then add monthly taxes and insurance separately.",
      fields: [
        { key: "balance", label: "Total outstanding balance", ...CURRENCY },
        { key: "rate", label: "Interest rate", ...RATE },
        // newRow: the step reads as "what is owed" (balance, rate) then "what is paid each
        // month" (P+I, taxes, insurance). Without it the two groups run together on one line.
        { key: "pi", label: "Mortgage P + I", ...PER_MONTH, newRow: true },
        { key: "taxes", label: "Taxes (Monthly)", ...PER_MONTH },
        { key: "insurance", label: "Insurance (Monthly)", ...PER_MONTH }
      ],
      // Deliberately the full housing outlay -- P+I plus escrows -- because the point of the
      // rollup is what leaves the borrower's account every month. The review line breaks the
      // three parts back out so nobody has to guess what the number contains.
      monthly: v => num(v.pi) + num(v.taxes) + num(v.insurance)
    },
    {
      key: "creditCards",
      nav: "Credit Cards",
      title: "Credit Cards",
      icon: "fa-light fa-credit-card",
      tone: "violet",
      description: "The estimated payment is calculated as monthly interest plus 1% of the outstanding balance.",
      defaults: { rate: "22.000" },
      fields: [
        { key: "balance", label: "Total outstanding balance", ...CURRENCY },
        { key: "rate", label: "Average interest rate", ...RATE },
        {
          key: "payment",
          label: "Estimated minimum monthly payment",
          ...PER_MONTH,
          estimated: true,
          info: "Estimated as (balance × rate ÷ 12) + 1% of balance. Type over it to use the borrower's actual minimum payment."
        }
      ],
      monthly: v => num(v.payment)
    },
    {
      key: "carLoans",
      nav: "Car Loans",
      title: "Car Loans",
      icon: "fa-light fa-car",
      tone: "teal",
      description: "Include monthly payments for every financed or leased vehicle.",
      fields: [
        { key: "balance", label: "Total outstanding balance", ...CURRENCY },
        { key: "rate", label: "Average interest rate", ...RATE },
        { key: "payment", label: "Total monthly car loan payments", ...PER_MONTH }
      ],
      monthly: v => num(v.payment)
    },
    {
      key: "personalLoans",
      nav: "Personal Loans",
      title: "Personal Loans",
      icon: "fa-light fa-hand-holding-dollar",
      tone: "amber",
      description: "Include all recurring payments for unsecured or secured personal loans.",
      fields: [
        { key: "balance", label: "Total outstanding balance", ...CURRENCY },
        { key: "rate", label: "Average interest rate", ...RATE },
        { key: "payment", label: "Total monthly personal loan payments", ...PER_MONTH }
      ],
      monthly: v => num(v.payment)
    },
    {
      key: "other",
      nav: "Other Payments",
      title: "Other Monthly Payments",
      icon: "fa-light fa-receipt",
      tone: "slate",
      description: "Enter any other recurring monthly debt payments not included in the categories above.",
      fields: [
        { key: "balance", label: "Total outstanding balance", ...CURRENCY },
        { key: "rate", label: "Average interest rate", ...RATE },
        { key: "payment", label: "Total other monthly payments", ...PER_MONTH }
      ],
      monthly: v => num(v.payment)
    }
  ]

  const TERMS = [30, 25, 20, 15, 10]
  const HELOC_TERMS = [30, 20, 15, 10, 5]

  function blankValues() {
    const out = {}
    for (const c of CATEGORIES) {
      out[c.key] = {}
      for (const f of c.fields) out[c.key][f.key] = (c.defaults && c.defaults[f.key]) || ""
    }
    return out
  }

  export default {
    name: "DebtConsolidationCalculator",
    components: { PageHeaderPortal },

    data() {
      return {
        categories: CATEGORIES,
        TERMS,
        HELOC_TERMS,
        phase: "debt",
        stepIndex: 0,
        maxVisited: 0,
        values: blankValues(),
        // A category counts as filled once the user has moved past it OR has typed anything
        // into it, so the rail fills in live while they are still on the step.
        visited: {},
        // Auto-filled fields (the credit-card minimum, and the phase-2 amounts seeded from
        // phase 1) stop being auto-filled the moment the user types over them. Without this,
        // going Back to fix a balance would silently wipe out their override.
        overridden: {},
        openInfo: "",
        copied: false,
        refi: { amount: "", rate: "7.500", term: 30, taxes: "", insurance: "" },
        heloc: { amount: "", rate: "9.000", structure: "pi", term: 10 }
      }
    },

    computed: {
      isReview() {
        return this.stepIndex >= this.categories.length
      },
      cat() {
        return this.categories[Math.min(this.stepIndex, this.categories.length - 1)]
      },
      percent() {
        if (this.isReview) return 100
        return Math.round(((this.stepIndex + 1) / this.categories.length) * 100)
      },
      filledCount() {
        return this.categories.filter(c => this.isFilled(c)).length
      },
      totalMonthly() {
        return this.categories.reduce((s, c) => s + (this.isFilled(c) ? this.monthlyFor(c) : 0), 0)
      },
      totalBalance() {
        return this.categories.reduce((s, c) => s + (this.isFilled(c) ? num(this.values[c.key].balance) : 0), 0)
      },
      // Everything except the mortgage -- what a HELOC would actually be paying off.
      nonMortgageBalance() {
        return this.categories
          .filter(c => c.key !== "mortgage")
          .reduce((s, c) => s + (this.isFilled(c) ? num(this.values[c.key].balance) : 0), 0)
      },
      currentBlendedRate() {
        return blendedRate(
          this.categories.map(c => ({
            balance: this.isFilled(c) ? num(this.values[c.key].balance) : 0,
            rate: num(this.values[c.key].rate)
          }))
        )
      },

      // ── Option 1: cash-out refinance ──
      newMortgage() {
        const amount = num(this.refi.amount)
        const pi = pmt(amount, num(this.refi.rate), Number(this.refi.term))
        const taxesMonthly = num(this.refi.taxes) / 12
        const insuranceMonthly = num(this.refi.insurance) / 12
        return { amount, pi, taxesMonthly, insuranceMonthly, totalMonthly: pi + taxesMonthly + insuranceMonthly }
      },
      refiDifference() {
        return this.totalMonthly - this.newMortgage.totalMonthly
      },
      // Same comparison for option 2: today's whole outlay against keeping the mortgage and
      // servicing a HELOC. Positive means the borrower pays less than they do now.
      helocDifference() {
        return this.totalMonthly - this.helocPlan.combinedMonthly
      },
      // What the borrower pays each month on everything except the house.
      nonMortgageMonthly() {
        return this.totalMonthly - this.monthlyFor(this.categories[0])
      },

      // ── Option 2: keep the mortgage, add a HELOC ──
      helocPlan() {
        const amount = num(this.heloc.amount)
        const r = num(this.heloc.rate)
        const payment =
          this.heloc.structure === "io" ? (amount * r) / 100 / 12 : pmt(amount, r, Number(this.heloc.term))
        const mortgageMonthly = this.monthlyFor(this.categories[0])
        const mortgageBalance = num(this.values.mortgage.balance)
        return {
          amount,
          payment,
          combinedMonthly: mortgageMonthly + payment,
          combinedBalance: mortgageBalance + amount,
          blendedRate: blendedRate([
            { balance: mortgageBalance, rate: num(this.values.mortgage.rate) },
            { balance: amount, rate: r }
          ])
        }
      },

      // ── Keep paying today's outlay, but against the new mortgage ──
      accel() {
        const required = this.newMortgage.totalMonthly
        const escrows = this.newMortgage.taxesMonthly + this.newMortgage.insuranceMonthly
        const outlay = this.totalMonthly
        const appliedToPi = Math.max(0, outlay - escrows)
        const months = required > 0 && outlay >= required
          ? monthsToPayoff(this.newMortgage.amount, num(this.refi.rate), appliedToPi)
          : null

        if (months == null) {
          return { available: false, appliedToPi, interestSaved: 0, payoffLabel: "", savedLabel: "" }
        }

        const baseMonths = Number(this.refi.term) * 12
        // Interest is total paid minus principal in both cases, so the principal cancels and
        // the difference is just the two payment streams. Kept on the FRACTIONAL month count,
        // because the real last payment is a partial one -- rounding up here would bill a
        // whole extra payment that never happens and understate the saving by ~one payment.
        const interestSaved = Math.max(0, this.newMortgage.pi * baseMonths - appliedToPi * months)
        // Displayed time, on the other hand, rounds UP: the borrower still writes a cheque in
        // month 195 even if it only clears a fifth of a payment.
        const wholeMonths = Math.ceil(months)
        return {
          available: true,
          appliedToPi,
          interestSaved,
          payoffLabel: monthsLabel(wholeMonths),
          savedLabel: monthsLabel(Math.max(0, baseMonths - wholeMonths))
        }
      }
    },

    methods: {
      num,

      // ── formatting ──
      money(n) {
        return "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      rate(n) {
        return (Number(n) || 0).toFixed(3) + "%"
      },
      formatField(f, raw) {
        if (!hasValue(raw)) return ""
        const n = num(raw)
        return f.kind === "rate"
          ? n.toFixed(f.decimals)
          : n.toLocaleString("en-US", { minimumFractionDigits: f.decimals, maximumFractionDigits: f.decimals })
      },
      formatMoneyField(raw) {
        return this.formatField(CURRENCY, raw)
      },
      formatRateField(raw) {
        return this.formatField(RATE, raw)
      },
      diffLabel(diff) {
        if (Math.abs(diff) < 0.005) return this.money(0)
        return this.money(Math.abs(diff)) + (diff > 0 ? " lower" : " higher")
      },
      diffTone(diff) {
        if (Math.abs(diff) < 0.005) return ""
        return diff > 0 ? "is-good" : "is-bad"
      },

      // ── phase 1 input ──
      onInput(cat, f, raw) {
        this.values[cat.key][f.key] = raw
        if (f.estimated) this.overridden[cat.key + "." + f.key] = true
        if (cat.key === "creditCards" && (f.key === "balance" || f.key === "rate")) this.applyCardEstimate()
        this.syncOptionSeeds()
      },
      onBlur(cat, f) {
        this.values[cat.key][f.key] = this.formatField(f, this.values[cat.key][f.key])
      },
      applyCardEstimate() {
        if (this.overridden["creditCards.payment"]) return
        const v = this.values.creditCards
        const bal = num(v.balance)
        if (!bal) {
          v.payment = ""
          return
        }
        // Monthly interest + 1% of principal -- the industry-standard minimum-payment proxy,
        // and the formula printed on the step itself so the number is never a black box.
        v.payment = (bal * (num(v.rate) / 100 / 12) + bal * 0.01).toFixed(2)
      },

      // ── phase 2 input ──
      // The three seeded fields keep tracking phase 1 until the user types in them. That is
      // what makes "Edit Current Debt" round-trip safely: change a balance, come back, and the
      // loan amount has followed -- unless you had deliberately set your own.
      onOptionInput(group, key, raw) {
        this[group][key] = raw
        this.overridden[group + "." + key] = true
      },
      syncOptionSeeds() {
        if (!this.overridden["refi.amount"]) this.refi.amount = this.formatMoneyField(String(this.totalBalance))
        if (!this.overridden["refi.taxes"]) {
          this.refi.taxes = this.formatMoneyField(String(num(this.values.mortgage.taxes) * 12))
        }
        if (!this.overridden["refi.insurance"]) {
          this.refi.insurance = this.formatMoneyField(String(num(this.values.mortgage.insurance) * 12))
        }
        if (!this.overridden["heloc.amount"]) this.heloc.amount = this.formatMoneyField(String(this.nonMortgageBalance))
      },

      // ── rollups ──
      monthlyFor(c) {
        return c.monthly(this.values[c.key])
      },
      isFilled(c) {
        if (this.visited[c.key]) return true
        const v = this.values[c.key]
        // A category's own default (the 22% card rate) must not count as the user filling it in.
        return c.fields.some(f => hasValue(v[f.key]) && v[f.key] !== ((c.defaults && c.defaults[f.key]) || null))
      },
      railSub(c) {
        if (!this.isFilled(c)) return "Outstanding balance"
        const v = this.values[c.key]
        return `${this.money(num(v.balance))} balance · ${this.rate(num(v.rate))}`
      },
      reviewDetail(c) {
        const v = this.values[c.key]
        const parts = [`${this.money(this.monthlyFor(c))} / month`, this.rate(num(v.rate))]
        if (c.key === "mortgage") {
          parts.push(`${this.money(num(v.taxes))} taxes`, `${this.money(num(v.insurance))} insurance`)
        }
        return parts.join(" · ")
      },

      // ── navigation ──
      goTo(i) {
        if (i > this.maxVisited) return
        this.stepIndex = i
        this.openInfo = ""
        this.copied = false
      },
      next() {
        if (this.isReview) {
          this.syncOptionSeeds()
          this.phase = "options"
          this.scrollTop()
          return
        }
        this.visited[this.cat.key] = true
        this.stepIndex += 1
        this.maxVisited = Math.max(this.maxVisited, Math.min(this.stepIndex, this.categories.length - 1))
        this.openInfo = ""
        this.syncOptionSeeds()
      },
      back() {
        if (this.stepIndex === 0) return
        this.stepIndex -= 1
        this.openInfo = ""
        this.copied = false
      },
      editCurrentDebt() {
        this.phase = "debt"
        this.stepIndex = this.categories.length
        this.copied = false
        this.scrollTop()
      },
      scrollTop() {
        this.$nextTick(() => {
          if (this.$el && this.$el.scrollTo) this.$el.scrollTo({ top: 0, behavior: "smooth" })
        })
      },
      scrollTo(ref) {
        const el = this.$refs[ref]
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" })
      },
      startOver() {
        this.values = blankValues()
        this.visited = {}
        this.overridden = {}
        this.refi = { amount: "", rate: "7.500", term: 30, taxes: "", insurance: "" }
        this.heloc = { amount: "", rate: "9.000", structure: "pi", term: 10 }
        this.phase = "debt"
        this.stepIndex = 0
        this.maxVisited = 0
        this.openInfo = ""
        this.copied = false
        this.scrollTop()
      },

      async copySummary() {
        const l = ["Debt Consolidation", "", "CURRENT DEBT"]
        for (const c of this.categories) {
          l.push(`  ${c.title}: ${this.money(this.monthlyFor(c))} / month — ${this.reviewDetail(c)}`)
        }
        l.push(`  Total monthly payments: ${this.money(this.totalMonthly)}`)
        l.push(`  Total outstanding balance: ${this.money(this.totalBalance)}`)
        l.push(`  Blended interest rate: ${this.rate(this.currentBlendedRate)}`)
        l.push("", "OPTION 1 — CASH OUT REFINANCE")
        l.push(`  New loan amount: ${this.money(this.newMortgage.amount)} at ${this.rate(num(this.refi.rate))} for ${this.refi.term} years`)
        l.push(`  New monthly payment: ${this.money(this.newMortgage.totalMonthly)} (P&I ${this.money(this.newMortgage.pi)})`)
        l.push(`  Monthly difference: ${this.diffLabel(this.refiDifference)}`)
        l.push("", "OPTION 2 — CURRENT MORTGAGE + HELOC")
        l.push(`  HELOC: ${this.money(this.helocPlan.amount)} at ${this.rate(num(this.heloc.rate))}, ${this.heloc.structure === "io" ? "interest only" : this.heloc.term + "-year P&I"}`)
        l.push(`  HELOC payment: ${this.money(this.helocPlan.payment)}`)
        l.push(`  Combined monthly payment: ${this.money(this.helocPlan.combinedMonthly)}`)
        l.push(`  Combined balance: ${this.money(this.helocPlan.combinedBalance)} at a blended ${this.rate(this.helocPlan.blendedRate)}`)
        l.push(`  Monthly difference: ${this.diffLabel(this.helocDifference)}`)
        l.push("", "ACCELERATED PAYMENT (same monthly outlay)")
        l.push(this.accel.available
          ? `  Payoff in ${this.accel.payoffLabel} — ${this.accel.savedLabel} sooner, about ${this.money(this.accel.interestSaved)} of interest saved`
          : `  Not available — the current outlay does not cover the ${this.money(this.newMortgage.totalMonthly)} required payment`)
        try {
          await navigator.clipboard.writeText(l.join("\n"))
          this.copied = true
          setTimeout(() => (this.copied = false), 2000)
        } catch (e) {
          // Clipboard is permission-gated and blocked outright in some embedded contexts; the
          // button simply does not confirm rather than throwing at the user.
          this.copied = false
        }
      }
    }
  }
</script>

<style scoped>
  /* Local tokens. This app has no shared design-token sheet, so -- like LoanPriceSheet --
     every var carries its own fallback and the fallback is what actually renders today.

     TYPE SCALE. Every font-size on this page comes from one of the --fs-* tokens below, and
     every one of those is derived from --calc-type-scale. If the page ever reads small (or
     large) again, change that ONE number and the whole page moves together, keeping the
     relationships between heading, body, label and figure intact. Do not hard-code a px
     font-size anywhere below -- that is how a scale drifts back out of tune one rule at a
     time. Spacing that has to track the type (control heights, icon tiles) scales with it too. */
  /* NOTE: both selectors, deliberately. PageHeaderPortal teleports .page-header-left out of
     .calc-page and into the app's top bar, so anything declared only on .calc-page is out of
     scope by the time the header renders -- custom properties inherit through the DOM, and the
     teleported node's DOM parent is header.vue's slot. Declaring the tokens on the header root
     as well is what keeps the navy tile navy once it has moved. Add a token here, not below. */
  .calc-page,
  .page-header-left {
    --calc-type-scale: 1.12;

    --fs-2xs: calc(11px * var(--calc-type-scale));   /* eyebrows, chips, micro-notes */
    --fs-xs: calc(12.5px * var(--calc-type-scale));  /* field labels, sublines */
    --fs-sm: calc(13.5px * var(--calc-type-scale));  /* secondary text, step nav */
    --fs-md: calc(14.5px * var(--calc-type-scale));  /* body copy, row names */
    --fs-base: calc(15.5px * var(--calc-type-scale));/* values the user types */
    --fs-lg: calc(17px * var(--calc-type-scale));    /* row amounts */
    --fs-xl: calc(20px * var(--calc-type-scale));    /* panel figures */
    --fs-2xl: calc(23px * var(--calc-type-scale));   /* card headings */
    --fs-3xl: calc(27px * var(--calc-type-scale));   /* comparison stat numbers */
    --fs-4xl: calc(31px * var(--calc-type-scale));   /* the hero payment */

    --control-h: calc(40px * var(--calc-type-scale));
    --icon-tile: calc(46px * var(--calc-type-scale));

    /* 2.0 palette, taken from LoRedirect ("My Website") so the calculators sit in the same
       family as the rest of the app: the same #123a71 navy on the page-icon tile, the same
       #eaeaea card edge and #7b8397 secondary text, and the same warm #fdf6e3 / #efdca8 /
       #a5822a highlight its "No website yet" callout uses. Gold comes in two weights because
       the brass that reads right on white disappears on the navy panels. */
    --calc-navy: #123a71;
    --calc-blue: #5f79c7;
    --calc-gold: #a5822a;
    --calc-gold-lt: #e9bd54;
    --calc-gold-bg: #fdf6e3;
    --calc-gold-edge: #efdca8;
    --calc-ink: #1f2a44;
    --calc-muted: #7b8397;
    --calc-dim: #9aa3b6;
    --calc-line: #eaeaea;
    --calc-card: #ffffff;
    --calc-soft: #f7f8fb;
  }

  .calc-page {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 18px;
    color: var(--calc-ink);
    background: var(--calc-soft);
    max-height: calc(100vh - 112px);
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
    padding-bottom: 96px;
  }

  .page-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  /* The page "logo": the same 48px navy tile the globe sits in on My Website. Fixed size and
     radius rather than scaled ones, because it has to line up with every other page's tile in
     the shared top bar -- the type scale is this page's business, that tile is the app's. */
  .page-icon {
    flex: 0 0 auto;
    width: 48px;
    height: 48px;
    border-radius: 5px;
    background: var(--calc-navy);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }

  .page-title {
    margin: 0;
    font-size: var(--fs-xl);
    font-weight: 700;
    color: var(--calc-ink);
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: var(--fs-sm);
    font-weight: 500;
    color: var(--calc-muted);
  }

  .page-card {
    background: var(--calc-card);
    border: 1px solid var(--calc-line);
    border-radius: 12px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
    padding: 20px;
  }

  .eyebrow {
    margin: 0;
    font-size: var(--fs-2xs);
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--calc-gold);
  }

  .eyebrow--gold {
    color: var(--calc-gold-lt);
  }

  /* ── progress ─────────────────────────────────────────────────────────────── */
  .progress-top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 10px;
  }

  .progress-step {
    font-size: var(--fs-md);
    font-weight: 800;
    color: var(--calc-navy);
  }

  .progress-pct {
    font-size: var(--fs-sm);
    font-weight: 700;
    color: var(--calc-muted);
  }

  .progress-track {
    height: 9px;
    border-radius: 999px;
    background: #e9edf4;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #dcb254, #b8912f);
    transition: width 0.25s ease;
  }

  .progress-steps {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 6px 18px;
    list-style: none;
    margin: 13px 0 0;
    padding: 0;
  }

  .progress-steps button {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    font-size: var(--fs-sm);
    font-weight: 600;
    color: var(--calc-dim);
    cursor: default;
  }

  .progress-steps .is-visited button {
    color: var(--calc-blue);
    cursor: pointer;
  }

  .progress-steps .is-visited button:hover {
    text-decoration: underline;
  }

  .progress-steps .is-current button {
    color: var(--calc-navy);
    font-weight: 800;
    cursor: default;
    text-decoration: none;
  }

  /* ── two-column body ──────────────────────────────────────────────────────── */
  .calc-body {
    display: grid;
    /* 430, not 340: measured, not guessed. At --calc-type-scale 1.12 the longest category
       name ("Current Mortgage Payment") needs ~232px of the rail's name column to stay on one
       line, and the numbered dot, the figure and the gaps take the rest. A longer name than
       that still wraps to two lines rather than clipping, which is the acceptable failure. */
    grid-template-columns: minmax(0, 1fr) 430px;
    gap: 16px;
    align-items: start;
  }

  .step-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* ── step head ────────────────────────────────────────────────────────────── */
  .step-head {
    display: flex;
    gap: 14px;
    align-items: flex-start;
  }

  .step-head-text {
    min-width: 0;
  }

  .step-icon {
    flex: 0 0 auto;
    width: var(--icon-tile);
    height: var(--icon-tile);
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: var(--fs-xl);
  }

  /* Category tiles pulled toward the 2.0 hues: the blue is the app's #5f79c7 accent family,
     the amber is the My Website callout itself, the rest sit at the same low saturation. */
  .tone-blue { background: #e9eefa; color: #34518f; }
  .tone-violet { background: #eeecfa; color: #574aa8; }
  .tone-teal { background: #e4f1f0; color: #2c7a76; }
  .tone-amber { background: var(--calc-gold-bg); color: var(--calc-gold); }
  .tone-slate { background: #eef1f7; color: #5b6a84; }
  .tone-green { background: #e6f2ec; color: #2f7a58; }

  .step-head h2 {
    margin: 4px 0 0;
    font-size: var(--fs-2xl);
    font-weight: 700;
  }

  .step-desc {
    margin: 7px 0 0;
    font-size: var(--fs-md);
    line-height: 1.5;
    color: var(--calc-muted);
    max-width: 62ch;
  }

  /* ── fields ───────────────────────────────────────────────────────────────── */
  /* Three to a row, fixed. auto-fit let the column count drift with the card width, so on a
     wide screen the mortgage step packed four fields onto line one and orphaned Insurance. */
  .field-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
    margin-top: 24px;
  }

  .field--new-row {
    grid-column-start: 1;
  }

  .field-grid--wide {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  .field--full {
    grid-column: 1 / -1;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
  }

  .field-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--fs-xs);
    font-weight: 700;
    color: var(--calc-muted);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .info-dot {
    width: 1.35em;
    height: 1.35em;
    border-radius: 50%;
    border: 1px solid #c3cad8;
    background: none;
    color: var(--calc-muted);
    font-size: var(--fs-2xs);
    font-weight: 800;
    font-style: italic;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    text-transform: none;
  }

  .info-dot:hover {
    border-color: var(--calc-blue);
    color: var(--calc-blue);
  }

  .input-shell {
    display: flex;
    align-items: center;
    height: var(--control-h);
    border: 1px solid var(--calc-line);
    border-radius: 8px;
    background: #ffffff;
    padding: 0 12px;
    gap: 6px;
  }

  .input-shell:focus-within {
    border-color: var(--calc-navy);
    box-shadow: 0 0 0 3px rgba(18, 58, 113, 0.14);
  }

  .input-shell input {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    border: 0;
    outline: none;
    background: transparent;
    font: inherit;
    font-size: var(--fs-base);
    font-weight: 600;
    color: var(--calc-ink);
    text-align: right;
  }

  .input-shell--select {
    padding: 0;
  }

  .input-shell--select select {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    border: 0;
    outline: none;
    background: transparent;
    font: inherit;
    font-size: var(--fs-base);
    font-weight: 600;
    color: var(--calc-ink);
    padding: 0 12px;
    cursor: pointer;
  }

  .input-shell--select select:disabled {
    color: var(--calc-dim);
    cursor: not-allowed;
  }

  .affix {
    flex: 0 0 auto;
    font-size: var(--fs-sm);
    font-weight: 700;
    color: var(--calc-dim);
  }

  .field-note {
    font-size: var(--fs-xs);
    line-height: 1.45;
    color: var(--calc-muted);
  }

  /* ── review ───────────────────────────────────────────────────────────────── */
  .review-list {
    list-style: none;
    margin: 24px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .review-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    border-radius: 9px;
    background: var(--calc-soft);
    border: 1px solid #eaeef5;
  }

  /* The category name holds its line; the detail string is the one that gives way, because a
     wrapped "$2,492.62 / month · 6.500% · ..." still reads fine while a wrapped row label
     breaks the alignment down the list. */
  .review-name {
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: var(--fs-md);
    font-weight: 700;
  }

  .review-detail {
    flex: 1 1 auto;
    min-width: 0;
    font-size: var(--fs-xs);
    color: var(--calc-muted);
    text-align: right;
  }

  .review-amount {
    flex: 0 0 auto;
    font-size: var(--fs-lg);
    font-weight: 800;
    white-space: nowrap;
  }

  .link-button {
    flex: 0 0 auto;
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    font-size: var(--fs-sm);
    font-weight: 700;
    color: var(--calc-blue);
    cursor: pointer;
  }

  .link-button:hover {
    text-decoration: underline;
  }

  /* ── footer ───────────────────────────────────────────────────────────────── */
  .step-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 28px;
    padding-top: 18px;
    border-top: 1px solid var(--calc-line);
  }

  .foot-right {
    display: flex;
    gap: 10px;
  }

  .primary-button,
  .secondary-button {
    height: var(--control-h);
    border-radius: 8px;
    padding: 0 22px;
    font-weight: 700;
    font-size: var(--fs-md);
    cursor: pointer;
    border: 1px solid transparent;
    white-space: nowrap;
  }

  .primary-button {
    background: var(--calc-navy);
    color: #ffffff;
  }

  .primary-button:hover {
    filter: brightness(1.18);
  }

  .secondary-button {
    background: var(--calc-card);
    color: var(--calc-navy);
    border-color: var(--calc-line);
  }

  .secondary-button:hover:not(:disabled) {
    background: var(--calc-soft);
  }

  .primary-button:disabled,
  .secondary-button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .icon-button {
    flex: 0 0 auto;
    width: 2.2em;
    height: 2.2em;
    border-radius: 7px;
    border: 1px solid var(--calc-line);
    background: var(--calc-card);
    color: var(--calc-navy);
    cursor: pointer;
    font-size: var(--fs-sm);
  }

  .icon-button:hover {
    background: var(--calc-soft);
  }

  /* ── rail ─────────────────────────────────────────────────────────────────── */
  .rail {
    padding: 0;
    overflow: hidden;
    position: sticky;
    top: 0;
  }

  .rail-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 18px 18px 12px;
  }

  .rail-head h3 {
    margin: 4px 0 0;
    font-size: var(--fs-lg);
    font-weight: 700;
  }

  .rail-chip {
    flex: 0 0 auto;
    font-size: var(--fs-2xs);
    font-weight: 800;
    color: var(--calc-muted);
    background: var(--calc-soft);
    border: 1px solid #e9edf4;
    border-radius: 999px;
    padding: 5px 10px;
  }

  .rail-list {
    list-style: none;
    margin: 0;
    padding: 0 18px 18px;
    display: flex;
    flex-direction: column;
  }

  /* A grid, not a flex row, so the balance subline gets a line of its own. As a flex row the
     subline ("$320,000.00 balance · 6.500%") is the widest thing in the right-hand column and
     sets its width, which squeezed a long category name like "Current Mortgage Payment" into
     three wrapped lines. Here the name and the amount share row 1 and the subline spans row 2,
     so the name only has to fit next to the figure. */
  .rail-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    column-gap: 11px;
    padding: 11px 0;
    border-top: 1px solid #eef1f6;
  }

  .rail-row:first-child {
    border-top: 0;
  }

  .rail-num {
    grid-row: 1 / span 2;
    width: 1.75em;
    height: 1.75em;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #eef1f6;
    color: var(--calc-muted);
    font-size: var(--fs-xs);
    font-weight: 800;
  }

  .rail-row.is-done .rail-num {
    background: #e6f2ec;
    color: #2f7a58;
  }

  .rail-name {
    grid-column: 2;
    min-width: 0;
    font-size: var(--fs-sm);
    font-weight: 700;
  }

  /* display: contents lifts the two children into the row's grid so they can be placed
     independently; the wrapper itself draws nothing. */
  .rail-right {
    display: contents;
  }

  .rail-right strong {
    grid-column: 3;
    grid-row: 1;
    text-align: right;
    font-size: var(--fs-md);
    font-weight: 800;
    white-space: nowrap;
  }

  .rail-right small {
    grid-column: 2 / span 2;
    grid-row: 2;
    text-align: right;
    font-size: var(--fs-2xs);
    color: var(--calc-muted);
  }

  .rail-foot {
    background: var(--calc-navy);
    color: #ffffff;
    padding: 16px 18px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .rail-foot span {
    display: block;
    font-size: var(--fs-xs);
    color: rgba(255, 255, 255, 0.72);
  }

  .rail-foot strong {
    display: block;
    margin-top: 4px;
    font-size: var(--fs-xl);
    font-weight: 800;
    white-space: nowrap;
  }

  /* ── dark rail (new payment) ──────────────────────────────────────────────── */
  .rail--dark {
    background: var(--calc-navy);
    border-color: var(--calc-navy);
    color: #ffffff;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .rail--dark .rail-head {
    padding: 0;
  }

  .rail--dark .rail-head h3 {
    color: #ffffff;
  }

  .rail-hero {
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 9px;
    padding: 16px 18px;
  }

  .rail-hero span {
    display: block;
    font-size: var(--fs-sm);
    color: rgba(255, 255, 255, 0.72);
  }

  .rail-hero strong {
    display: block;
    margin-top: 6px;
    font-size: var(--fs-4xl);
    font-weight: 800;
    letter-spacing: -0.01em;
  }

  .rail-rows {
    margin: 0;
    display: flex;
    flex-direction: column;
  }

  .rail-rows > div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  }

  .rail-rows > div:last-child {
    border-bottom: 0;
  }

  .rail-rows dt {
    font-size: var(--fs-sm);
    color: rgba(255, 255, 255, 0.72);
  }

  .rail-rows dd {
    margin: 0;
    font-size: var(--fs-md);
    font-weight: 800;
    white-space: nowrap;
  }

  .rail-rows--divided {
    margin-top: auto;
    border-top: 1px solid rgba(255, 255, 255, 0.16);
  }

  .rail-savings {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    background: rgba(255, 255, 255, 0.09);
    border-radius: 9px;
    padding: 14px 18px;
  }

  .rail-savings span {
    font-size: var(--fs-sm);
    color: rgba(255, 255, 255, 0.84);
  }

  .rail-savings strong {
    font-size: var(--fs-xl);
    font-weight: 800;
    color: var(--calc-gold-lt);
    white-space: nowrap;
  }

  /* ── options head / banner ────────────────────────────────────────────────── */
  .options-head,
  .banner-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .options-head h2,
  .banner-card h2 {
    margin: 4px 0 0;
    font-size: var(--fs-2xl);
    font-weight: 700;
  }

  .banner-card {
    display: block;
  }

  /* ── comparison ───────────────────────────────────────────────────────────── */
  .compare-title {
    margin: 4px 0 20px;
    font-size: var(--fs-2xl);
    font-weight: 700;
  }

  .accel {
    background: linear-gradient(135deg, #10305e, #1b4a86);
    border-top: 3px solid var(--calc-gold-lt);
    border-radius: 10px;
    padding: 20px;
    color: #ffffff;
  }

  .accel-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 18px;
  }

  .accel-head h3 {
    margin: 4px 0 0;
    font-size: var(--fs-xl);
    font-weight: 700;
  }

  .accel-chip {
    flex: 0 0 auto;
    font-size: var(--fs-2xs);
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--calc-gold-lt);
    border: 1px solid rgba(233, 189, 84, 0.5);
    border-radius: 999px;
    padding: 7px 14px;
  }

  .accel-grid {
    display: grid;
    grid-template-columns: 1.6fr repeat(4, 1fr);
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 8px;
    overflow: hidden;
  }

  .accel-cell {
    padding: 16px 18px;
    border-left: 1px solid rgba(255, 255, 255, 0.09);
    min-width: 0;
  }

  .accel-cell:first-child {
    border-left: 0;
  }

  .accel-label {
    display: block;
    font-size: var(--fs-xs);
    color: rgba(255, 255, 255, 0.68);
  }

  .accel-value {
    display: block;
    margin-top: 9px;
    font-size: var(--fs-xl);
    font-weight: 800;
  }

  .accel-value--sm {
    font-size: var(--fs-lg);
  }

  .accel-value.is-na {
    color: var(--calc-gold-lt);
  }

  .accel-cell small {
    display: block;
    margin-top: 7px;
    font-size: var(--fs-2xs);
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.6);
  }

  .accel-foot {
    margin: 15px 0 0;
    font-size: var(--fs-xs);
    color: rgba(255, 255, 255, 0.62);
  }

  .compare-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-top: 16px;
  }

  .compare-col {
    border: 1px solid var(--calc-line);
    border-radius: 10px;
    padding: 18px;
    min-width: 0;
  }

  /* Option 1 is the headline recommendation, so it gets the warm tint the mock uses to pull
     the eye to it -- nothing functional hangs off this. */
  .compare-col--option1 {
    background: var(--calc-gold-bg);
    border-color: var(--calc-gold-edge);
  }

  .compare-col-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    min-height: 3em;
  }

  .compare-col-head h4 {
    margin: 4px 0 0;
    font-size: var(--fs-lg);
    font-weight: 700;
  }

  .stat-box {
    background: var(--calc-navy);
    color: #ffffff;
    border-radius: 9px;
    padding: 16px 18px;
    margin-top: 12px;
  }

  .stat-box span {
    display: block;
    font-size: var(--fs-sm);
    color: rgba(255, 255, 255, 0.72);
  }

  .stat-box strong {
    display: block;
    margin-top: 6px;
    font-size: var(--fs-3xl);
    font-weight: 800;
    letter-spacing: -0.01em;
  }

  .compare-rows {
    margin: 4px 0 0;
  }

  .compare-rows > div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 11px 0;
    border-bottom: 1px solid #eef1f6;
  }

  .compare-rows > div:last-child {
    border-bottom: 0;
  }

  .compare-rows dt {
    font-size: var(--fs-sm);
    color: var(--calc-muted);
  }

  .compare-rows dd {
    margin: 0;
    font-size: var(--fs-md);
    font-weight: 800;
    white-space: nowrap;
  }

  .compare-rows dd.is-good { color: #1f7a4d; }
  .compare-rows dd.is-bad { color: #a4472f; }

  /* ── heloc ────────────────────────────────────────────────────────────────── */
  .heloc-split {
    display: grid;
    grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
    gap: 28px;
    align-items: start;
  }

  .heloc-intro h2 {
    margin: 4px 0 0;
    font-size: var(--fs-2xl);
    font-weight: 700;
  }

  .heloc-intro .eyebrow {
    margin-top: 15px;
  }

  .heloc-split .field-grid {
    margin-top: 0;
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  /* ── narrow ───────────────────────────────────────────────────────────────── */
  @media (max-width: 1340px) {
    .accel-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .accel-cell {
      border-left: 1px solid rgba(255, 255, 255, 0.09);
      border-top: 1px solid rgba(255, 255, 255, 0.09);
    }

    .accel-cell:nth-child(odd) {
      border-left: 0;
    }

    .accel-cell:nth-child(-n + 2) {
      border-top: 0;
    }

    .compare-grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  @media (max-width: 1160px) {
    .calc-body {
      grid-template-columns: minmax(0, 1fr);
    }

    .rail {
      position: static;
    }

    .heloc-split {
      grid-template-columns: minmax(0, 1fr);
      gap: 18px;
    }
  }

  @media (max-width: 760px) {
    .review-row {
      flex-wrap: wrap;
    }

    .review-name {
      flex: 1 1 100%;
    }

    .review-detail {
      flex: 1 1 auto;
      text-align: left;
    }

    .field-grid,
    .field-grid--wide,
    .heloc-split .field-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .field--new-row {
      grid-column-start: auto;
    }

    .accel-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .accel-cell {
      border-left: 0;
    }

    .accel-cell:first-child {
      border-top: 0;
    }
  }

  @media (max-width: 640px) {
    /* One step down on phones: the scale that reads right on a desktop panel is too big for a
       phone column, and moving the knob keeps every relationship on the page intact. Both
       roots again -- see the note on the token block. */
    .calc-page,
    .page-header-left {
      --calc-type-scale: 1;
    }

    .step-foot {
      flex-direction: column-reverse;
      align-items: stretch;
    }

    .foot-right {
      flex-direction: column-reverse;
    }

    .primary-button,
    .secondary-button {
      width: 100%;
    }

    .rail-foot {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
